// Copyright (c) StrangeBee 2023

import { ReactElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { useLocalMessage } from '@Helpers/locale';
import { FormatDate } from '@Molecules/Format';
import { ColumnsType } from 'antd/lib/table';
import { ICONS } from '@Constants/iconsApp';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { THList } from '@Molecules/THList';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { formatFileSize } from '@Helpers/stats';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { copyToClipboard } from '@Helpers/copy';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { AttachmentType, TaskLogOutput } from 'thehive-sdk';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { usePreview } from '@Molecules/THPreview';

export type AttachmentDataType = AttachmentType & {
    extraData: TaskLogOutput['extraData'];
    logId: TaskLogOutput['_id'];
};

interface AttachmentListProps {
    dataSource?: TaskLogOutput[];
    caseId: string;
    status: string;
    hasPermissions?: boolean;
    getDownloadUrl: (attachmentId: string, logId: string) => string;
    handlePreview: (src: AttachmentDataType) => void;
}

export const AttachmentTasksList = ({
    dataSource = [],
    caseId,
    status,
    getDownloadUrl,
    handlePreview,
}: AttachmentListProps): ReactElement => {
    const i = useLocalMessage();
    const { allowPreview } = usePreview();

    const data: AttachmentDataType[] = useMemo(
        () =>
            dataSource.flatMap(({ attachments, extraData, _id }) =>
                attachments.map((attachment) => {
                    return { ...attachment, extraData, logId: _id };
                }),
            ),
        [dataSource],
    );

    const columns = useMemo<ColumnsType<AttachmentDataType>>(
        () =>
            columnsRenderer([
                {
                    title: <ColumnTitle>{i('case.attachment.table.filename')}</ColumnTitle>,
                    className: 'case-attachment-list__column-filename custom-table__cell',
                    render: (attachement): ReactElement => {
                        const { name, contentType } = attachement;

                        return allowPreview(contentType) ? (
                            <THButton type="link" onClick={() => handlePreview(attachement)}>
                                {name}
                            </THButton>
                        ) : (
                            <>{name}</>
                        );
                    },
                },
                {
                    title: <ColumnTitle>{i('case.attachment.table.task')}</ColumnTitle>,
                    className: 'case-attachment-list__column-task custom-table__cell',
                    render: ({ extraData }): ReactElement => (
                        <Link to={RouteBuilder.compute('cases.task', { caseId, taskId: extraData?.taskId })}>
                            {extraData.task.title}
                        </Link>
                    ),
                },
                {
                    title: <ColumnTitle>{i('case.attachment.table.type')}</ColumnTitle>,
                    className: 'case-attachment-list__column-type custom-table__cell',
                    render: ({ contentType }): ReactElement => <span>{contentType}</span>,
                },
                {
                    title: <ColumnTitle>{i('case.attachment.table.size')}</ColumnTitle>,
                    className: 'case-attachment-list__column-size custom-table__cell',
                    render: ({ size }): ReactElement => <span>{formatFileSize(size || 0)}</span>,
                },
                {
                    title: <ColumnTitle>{i('case.attachment.table.date')}</ColumnTitle>,
                    className: 'case-attachment-list__column-date custom-table__cell',
                    render: ({ _createdAt }): ReactElement => <FormatDate date={_createdAt} />,
                },
                {
                    className: 'case-attachment-list__column-download custom-table__cell',
                    render: ({ _id, logId }): ReactElement => (
                        <ActionsList
                            actionList={{
                                copy: {
                                    label: i('general.copy-url'),
                                    icon: <ICONS.CopyOutlined />,
                                    query: () =>
                                        copyToClipboard(getDownloadUrl(_id || '', logId), i('notification.copy')),
                                },
                                export: {
                                    label: i('general.download'),
                                    icon: <ICONS.DownloadOutlined />,
                                    CustomComponent: (triggerElem): ReactElement => (
                                        <ExternalLink url={getDownloadUrl(_id || '', logId)}>
                                            {triggerElem}
                                        </ExternalLink>
                                    ),
                                },
                            }}
                            scope="single"
                            isIcon
                        />
                    ),
                },
            ]),
        [caseId, i, getDownloadUrl], // eslint-disable-line
    );

    return (
        <THList.Content
            dataSource={data}
            columns={columns}
            rowKey="_id"
            emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('case.attachment.none')} />}
        />
    );
};
